import { makeTabbable } from './utilities/functions';

/**
 * Mobile menu
 * @package: Abbey Vets
 * @author: Phil Steer (shffld)
 */
export default class MobileMenu {
    /**
     * Fail out if some args aren't present,
     * otherwise set up some internal stuff
     * @param {element} [container] overall nav container element
     * @param {element} [toggle] button to toggle open/close menu functionality
     * @param {element} [navList] the list elem that contains our menu links
     * @param {string} [navLinkClass] <a> className
     * @param {string} [togglePressedClass] toggle className if menu is open
     * @param {string} [navListActiveClass] nav list className if menu is open
     * @param {element} [navStatus] Element to add closed/open text to
     *
     * @return {void}
     */
    constructor (
        container,
        toggle,
        navList,
        navLinkClass,
        togglePressedClass,
        navListActiveClass,
        navStatus
    ) {
        if (!toggle || !container) {
            return false;
        }

        this.container = container;
        this.toggle = toggle;
        this.navList = navList;
        this.navLinkClass = navLinkClass;
        this.togglePressedClass = togglePressedClass;
        this.navListActiveClass = navListActiveClass;
        this.navStatus = navStatus;
        this.navHeight = 0;

        this.navTabbableItems = [
            ...this.container.querySelectorAll(this.navLinkClass),
        ];
    }

    /**
     * Do initial set up
     * @return {void}
     */
    init () {
        this.setNavHeight();
        this.close();
        this.addClickEvents();
    }

    /**
     * Work out how tall in px the mobile nav is and save on data attribute
     * @return {void}
     */
    setNavHeight () {
        this.navList.removeAttribute('style');
        this.navHeight = this.navList.offsetHeight;
        this.navList.setAttribute('data-height', this.navHeight);
    }

    /**
     * Add a click event to the toggle if it we can't find a data attribute
     * that suggests we've already done it
     * @return {void}
     */
    addClickEvents () {
        if (!this.toggle.getAttribute('data-listener-added')) {
            this.toggle.addEventListener('click', () => {
                if (this.toggle.classList.contains(this.togglePressedClass)) {
                    this.close();
                } else {
                    this.open();
                }
            });

            this.toggle.setAttribute('data-listener-added', true);
        }
    }

    /**
     * Open menu, do a11y stuff, change classes, etc and make links tabbable
     * @return {void}
     */
    open () {
        this.toggle.classList.add(this.togglePressedClass);
        this.toggle.setAttribute('aria-pressed', true);

        this.navList.classList.add(this.navListActiveClass);
        this.navList.setAttribute('aria-hidden', false);
        this.navStatus.innerHTML = 'open';

        this.navList.style.height =
            `${this.navList.getAttribute('data-height')}px`
        ;

        makeTabbable(this.navTabbableItems, false);
    }

    /**
     * Close menu, do a11y stuff, remove classes, etc and make links untabbable
     * @return {void}
     */
    close () {
        this.toggle.classList.remove(this.togglePressedClass);
        this.toggle.setAttribute('aria-pressed', false);

        this.navList.classList.remove(this.navListActiveClass);
        this.navList.setAttribute('aria-hidden', true);
        this.navStatus.innerHTML = 'closed';
        this.navList.style.height = 0;

        makeTabbable(false, this.navTabbableItems);
    }

    /**
     * Similar to close, but we want to remove inline styles, attributes
     * @return {void}
     */
    destroy () {
        this.toggle.classList.remove(this.togglePressedClass);
        this.toggle.setAttribute('aria-pressed', false);

        this.navList.classList.remove(this.navListActiveClass);
        this.navList.removeAttribute('aria-hidden');
        this.navList.removeAttribute('style');
        this.navList.removeAttribute('data-height');

        makeTabbable(this.navTabbableItems, false);
    }
}
